import {
  AuthenticationMethod,
  AuthenticationProcessTelecomType,
  CaptchaOptions,
  CardinalApis,
  CardinalBaseSdk,
  CardinalSdk,
  CryptoStrategies,
  DataOwnerWithType,
  HealthcareParty,
  KeyPairRecoverer,
  RecoveryDataKey,
  RecoveryDataUseFailureReason,
  RecoveryKeyOptions,
  RecoveryKeySize,
  RecoveryResult,
  Solution,
  SpkiHexString,
  StorageFacade,
  User,
  XCryptoService,
  XRsaKeypair,
} from '@icure/cardinal-sdk'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Unsubscribe } from 'redux'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { revertAll, setSavedCredentials } from '../app'
import { store } from '../store'
import { MSG_GW_URL, ICURE_CLOUD_URL, PROCESS_ID, SPEC_ID } from '../../constants'
import { startReplications } from '../api/interopApi'
import { RecoveryKeyOutcomeEnum } from '../../helpers/types'

const apiCache: { [key: string]: CardinalSdk } = {}

export interface MissingKeysOwner {
  dataOwnerId: string
  name?: string
  isSelf: boolean
  missingKeys: number
}

const dataOwnerName = ({ dataOwner }: DataOwnerWithType): string | undefined =>
  ('name' in dataOwner && dataOwner.name) || ('firstName' in dataOwner && [dataOwner.firstName, dataOwner.lastName].filter((x) => !!x).join(' ')) || undefined

if (typeof window !== 'undefined') {
  ;(window as any).__API_CACHE__ = apiCache
}

export class PreventiCryptoStrategies extends CryptoStrategies {
  // Set when the user skips the recovery while keys of a parent data owner are still missing: the SDK cannot then initialise in hierarchical mode
  parentKeysSkipped = false
  // When set, the recovery keys already provided by the user are re-applied without prompting again
  replayProvidedRecoveryKeys = false
  private providedRecoveryKeys: string[] = []

  async notifyNewKeyCreated(sdk: CardinalApis): Promise<void> {
    const recoveryKey = await sdk.recovery.createRecoveryInfoForAvailableKeyPairs({
      includeParentsKeys: true,
      recoveryKeyOptions: new RecoveryKeyOptions.Generate({ recoveryKeySize: RecoveryKeySize.Bytes32 }),
    })

    const formattedKey = recoveryKey
      .asBase32()
      .match(/.{1,4}/g)
      ?.join('-')

    formattedKey && store.dispatch(setNewlyCreatedRecoveryKey({ recoveryKey: formattedKey }))
  }

  async recoverAndVerifySelfHierarchyKeys(
    keysData: Array<CryptoStrategies.KeyDataRecoveryRequest>,
    cryptoPrimitives: XCryptoService,
    keyPairRecoverer: KeyPairRecoverer,
  ): Promise<{ [dataOwnerId: string]: CryptoStrategies.RecoveredKeyData }> {
    // Merge all successful recoveries here
    const allRecoveredKeys: { [dataOwnerId: string]: { [pub: SpkiHexString]: XRsaKeypair } } = {}
    const allRecoverableKeys: { [dataOwnerId: string]: SpkiHexString[] } = {}

    const computeResult = (keysData: Array<CryptoStrategies.KeyDataRecoveryRequest>, allRecoveredKeys: { [dataOwnerId: string]: { [pub: SpkiHexString]: XRsaKeypair } }) =>
      Object.fromEntries(
        keysData.map((kd) => {
          const dataOwnerId = kd.dataOwnerDetails.dataOwner.id

          return [
            dataOwnerId,
            {
              recoveredKeys: allRecoveredKeys[dataOwnerId] ?? {},
              keyAuthenticity: {
                ...Object.fromEntries(kd.unknownKeys.map((uk) => [uk, true])),
                ...Object.fromEntries(kd.unavailableKeys.map((uk) => [uk.publicKey, true])),
              },
            },
          ]
        }),
      )

    let replayed = false
    while (true) {
      const reasons: RecoveryDataUseFailureReason[] = []
      const missingKeysByOwner: MissingKeysOwner[] = []
      // keysData is ordered from the topmost parent to the current data owner (last element)
      keysData.forEach((keyData, idx) => {
        const dataOwnerId = keyData.dataOwnerDetails.dataOwner.id
        const missingKeys = keyData.unavailableKeys.filter(
          (unavailableKey) => !allRecoveredKeys?.[dataOwnerId]?.[unavailableKey.publicKey] && !allRecoverableKeys?.[dataOwnerId]?.includes(unavailableKey.publicKey),
        ).length
        if (missingKeys > 0) {
          missingKeysByOwner.push({ dataOwnerId, name: dataOwnerName(keyData.dataOwnerDetails), isSelf: idx === keysData.length - 1, missingKeys })
          reasons.push(...Array(missingKeys).fill(RecoveryDataUseFailureReason.Missing))
        }
      })

      if (reasons.length === 0) {
        store.dispatch(hideRecoveryKeysWindow())
        return computeResult(keysData, allRecoveredKeys)
      }

      let recoveryKeys: string[] | undefined
      if (this.replayProvidedRecoveryKeys) {
        recoveryKeys = replayed ? undefined : this.providedRecoveryKeys
        replayed = true
      } else {
        recoveryKeys = await this.promptUserForRecoveryKeys(reasons, missingKeysByOwner)
        this.providedRecoveryKeys.push(...(recoveryKeys ?? []))
      }

      for (const rk of recoveryKeys ?? []) {
        let decoded: RecoveryDataKey | undefined
        try {
          decoded = RecoveryDataKey.fromBase32(rk)
        } catch (e) {
          console.warn('Invalid recovery key, skipping:', e)
          store.dispatch(
            addTriedRecoveryKeyWithOutcome({
              key: rk,
              outcome: RecoveryKeyOutcomeEnum.INVALID_KEY,
            }),
          )
          continue
        }

        const res = await keyPairRecoverer.recoverWithRecoveryKey(decoded, false)

        if (res instanceof RecoveryResult.Success) {
          if (!Object.keys(res.data).some((dataOwnerId) => keysData.find((kd) => kd.dataOwnerDetails.dataOwner.id === dataOwnerId))) {
            store.dispatch(
              addTriedRecoveryKeyWithOutcome({
                key: rk,
                outcome: RecoveryKeyOutcomeEnum.NO_RECOVERY,
              }),
            )
          }

          let outcome: RecoveryKeyOutcomeEnum = RecoveryKeyOutcomeEnum.RECOVERY_ALREADY_DONE
          const recoveredByDataOwnerIdAndPublicKey = res.data
          for (const dataOwnerId of Object.keys(recoveredByDataOwnerIdAndPublicKey)) {
            allRecoveredKeys[dataOwnerId] = allRecoveredKeys[dataOwnerId] ?? {}
            allRecoverableKeys[dataOwnerId] = allRecoverableKeys[dataOwnerId] ?? []

            const perOwner = recoveredByDataOwnerIdAndPublicKey[dataOwnerId]
            for (const pub of Object.keys(perOwner)) {
              if (!allRecoveredKeys[dataOwnerId][pub as SpkiHexString]) {
                outcome = RecoveryKeyOutcomeEnum.RECOVERY
                allRecoveredKeys[dataOwnerId][pub as SpkiHexString] = perOwner[pub as SpkiHexString]
              }
            }

            const dataOwnerWithType = keysData?.find((kd) => kd.dataOwnerDetails.dataOwner.id === dataOwnerId)?.dataOwnerDetails
            if (!!dataOwnerWithType) {
              allRecoverableKeys[dataOwnerId] = allRecoverableKeys[dataOwnerId].concat(
                (await keyPairRecoverer.getRecoverableWithEncryptionKeys(dataOwnerWithType, Object.values(perOwner))).filter((k) => !allRecoverableKeys[dataOwnerId]?.includes(k)),
              )
            }
          }

          store.dispatch(
            addTriedRecoveryKeyWithOutcome({
              key: rk,
              outcome,
            }),
          )
        } else {
          store.dispatch(
            addTriedRecoveryKeyWithOutcome({
              key: rk,
              outcome: RecoveryKeyOutcomeEnum.INVALID_KEY,
            }),
          )
        }
        // If you want to surface per-key failure reasons, collect them here for telemetry/UI.
      }

      if (recoveryKeys === undefined) {
        // User skipped
        this.parentKeysSkipped = this.parentKeysSkipped || missingKeysByOwner.some(({ isSelf }) => !isSelf)
        return computeResult(keysData, allRecoveredKeys)
      }
    }
  }

  private async promptUserForRecoveryKeys(reasons: RecoveryDataUseFailureReason[], missingKeysByOwner: MissingKeysOwner[]): Promise<string[] | undefined> {
    const promise = new Promise<string[] | undefined>((resolve) => {
      // eslint-disable-next-line prefer-const
      let unsubscribe: Unsubscribe | undefined
      const handleChange = () => {
        const {
          cardinalApi: { recoveryKeys, recoveryKeyRequest },
        } = store.getState()
        if (!recoveryKeys?.length) {
          // The following happens in case of skip
          if (!recoveryKeyRequest) {
            resolve(undefined)
            unsubscribe?.()
          }
          return
        }
        const normalized = recoveryKeys.map((k) => k.replace(/-/g, '').replace(/0/g, 'O').replace(/1/g, 'I').replace(/8/g, 'B'))
        resolve(normalized)
        unsubscribe?.()
      }
      unsubscribe = store.subscribe(handleChange)
    })

    store.dispatch(askForRecoveryKeys({ reasons, missingKeysByOwner }))
    return promise
  }
}

export interface CardinalApiState {
  email?: string
  token?: string
  user?: User
  healthcareParty?: HealthcareParty
  keyPair?: { publicKey: string; privateKey: string }
  authProcess?: CardinalBaseSdk.BaseAuthenticationWithProcessStep
  online: boolean
  invalidEmail: boolean
  invalidToken: boolean
  waitingForToken: boolean
  firstName?: string
  lastName?: string
  dateOfBirth?: number
  mobilePhone?: string
  loginProcessStarted: boolean
  newlyCreatedRecoveryKey?: string
  recoveryKeyRequest?: { reasons: string[]; missingKeysByOwner: MissingKeysOwner[] }
  recoveryKeys?: string[]
  triedRecoveryKeys?: { [key: string]: RecoveryKeyOutcomeEnum }
  ehealthPassphrase?: string
}

const cardinalApiInitialState: CardinalApiState = {
  email: undefined,
  token: undefined,
  user: undefined,
  keyPair: undefined,
  authProcess: undefined,
  online: false,
  invalidEmail: false,
  invalidToken: false,
  waitingForToken: false,
  firstName: undefined,
  lastName: undefined,
  dateOfBirth: undefined,
  mobilePhone: undefined,
  loginProcessStarted: false,
  newlyCreatedRecoveryKey: undefined,
  recoveryKeyRequest: undefined,
  recoveryKeys: undefined,
}

function getError(e: Error): FetchBaseQueryError {
  return { status: 'CUSTOM_ERROR', error: e.message, data: e }
}

export async function guard<T, A, B, C, D, E, F, G = undefined>(
  guardedInputs: [A?, B?, C?, D?, E?, F?],
  lambda: (constrainedInputs: [A, B, C, D, E, F]) => Promise<T>,
  fallback: G = undefined as G,
): Promise<{ error: FetchBaseQueryError } | { data: T | G }> {
  if (guardedInputs.some((x) => !x)) {
    return { data: fallback }
  }
  try {
    const res = await lambda(guardedInputs as [A, B, C, D, E, F])
    const curate = (result: T): T => {
      if (result === null || result === undefined) {
        return null as T
      } else if (Array.isArray(result)) {
        return result.map(curate) as T
      } else {
        return result as T
      }
    }
    return { data: curate(res) }
  } catch (e) {
    console.error(e)
    return { error: getError(e as Error) }
  }
}

export const getApiFromState = async (getState: () => CardinalApiState | { cardinalApi: CardinalApiState } | undefined): Promise<CardinalSdk | undefined> => {
  const state = getState()
  if (!state) {
    throw new Error('No state found')
  }

  const initialState = 'cardinalApi' in state ? state.cardinalApi : state
  const { user } = initialState

  if (!user) {
    return undefined
  }

  return apiCache[`${user.groupId}/${user.id}`] as CardinalSdk
}

export const cardinalApi = async (getState: () => unknown) => {
  const state = getState() as { cardinalApi: CardinalApiState }
  return await getApiFromState(() => state)
}

// Initialises the SDK with the parent data owners' keys. If the user skipped the recovery of a parent's keys the hierarchical
// initialisation fails, so we fall back on a session limited to the user's own data instead of sending them back to the login screen.
const initializeSdk = async (login: string, token: string): Promise<CardinalSdk> => {
  const cryptoStrategies = new PreventiCryptoStrategies()
  const initialize = (useHierarchicalDataOwners: boolean) =>
    CardinalSdk.initialize(undefined, ICURE_CLOUD_URL, new AuthenticationMethod.UsingCredentials.UsernamePassword(login, token), StorageFacade.usingBrowserLocalStorage(), {
      useHierarchicalDataOwners,
      cryptoStrategies,
    })

  try {
    return await initialize(true)
  } catch (e) {
    if (!cryptoStrategies.parentKeysSkipped) {
      throw e
    }
    console.warn(`Parent keys were skipped, falling back to a non-hierarchical session: ${e}`)
    cryptoStrategies.replayProvidedRecoveryKeys = true
    return await initialize(false)
  }
}

export const startAuthentication = createAsyncThunk(
  'cardinalApi/startAuthentication',
  async (
    _payload: {
      captchaToken: Solution
    },
    { getState, dispatch },
  ) => {
    const {
      cardinalApi: { email, firstName, lastName },
    } = getState() as { cardinalApi: CardinalApiState }
    dispatch(setLoginProcessStarted(true))

    if (!email) {
      throw new Error('The email was not found')
    }

    try {
      // Only authenticates: the crypto initialisation happens in completeAuthentication, with a long-lived token it can retry with
      const authenticationStep = await CardinalBaseSdk.initializeWithProcess(
        undefined,
        ICURE_CLOUD_URL,
        MSG_GW_URL,
        SPEC_ID!,
        PROCESS_ID!,
        AuthenticationProcessTelecomType.Email,
        email,
        new CaptchaOptions.Kerberus.Computed({ solution: _payload.captchaToken }),
        { firstName, lastName },
      )

      dispatch(setLoginProcessStarted(false))
      return authenticationStep
    } catch (e) {
      console.error(`Couldn't start authentication: ${e}`)
    } finally {
      dispatch(setLoginProcessStarted(false))
    }
  },
)

export const completeAuthentication = createAsyncThunk('cardinalApi/completeAuthentication', async (_payload, { getState, dispatch }) => {
  const {
    cardinalApi: { authProcess, token },
  } = getState() as { cardinalApi: CardinalApiState }
  dispatch(setLoginProcessStarted(true))

  if (!authProcess) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No authProcess provided')
  }

  if (!token) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No token provided')
  }
  try {
    const baseApi = await authProcess.completeAuthentication(token)
    const user = await baseApi.user.getCurrentUser()
    const newToken = await baseApi.user.getToken(user.id, 'rememberMe')
    const api = await initializeSdk(`${user.groupId}/${user.id}`, newToken)
    const healthcareParty = await api.healthcareParty.getCurrentHealthcareParty()

    apiCache[`${user.groupId}/${user.id}`] = api

    startReplications(api)

    dispatch(
      setSavedCredentials({
        login: `${user.groupId}/${user.id}`,
        token: newToken,
        tokenTimestamp: +Date.now(),
      }),
    )
    return { user: new User(user), healthcareParty }
  } catch (e) {
    console.error(`Couldn't complete authentication: ${e}`)
    throw e
  } finally {
    dispatch(setLoginProcessStarted(false))
  }
})

export const login = createAsyncThunk('cardinalApi/login', async (_, { getState, dispatch }) => {
  const {
    cardinalApi: { email, token },
  } = getState() as { cardinalApi: CardinalApiState }
  dispatch(setLoginProcessStarted(true))

  if (!email) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No email provided')
  }

  if (!token) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No token provided')
  }

  try {
    const api = await initializeSdk(email, token)

    const user = await api.user.getCurrentUser()
    const healthcareParty = await api.healthcareParty.getCurrentHealthcareParty()

    apiCache[`${user.groupId}/${user.id}`] = api

    startReplications(api)

    return { user: new User(user), healthcareParty }
  } catch (e) {
    console.error(`Couldn't login: ${e}`)
    dispatch(revertAll())
    dispatch(resetCredentials())
  } finally {
    dispatch(setLoginProcessStarted(false))
  }
  return {}
})

export const logout = createAsyncThunk('cardinalApi/logout', async (_payload, { dispatch }) => {
  dispatch(revertAll())
  dispatch(resetCredentials())
})

export const cardinalApiRtk = createSlice({
  name: 'cardinalApi',
  initialState: cardinalApiInitialState,
  reducers: {
    setNewlyCreatedRecoveryKey: (state, { payload: { recoveryKey } }: PayloadAction<{ recoveryKey: string | undefined }>) => {
      state.newlyCreatedRecoveryKey = recoveryKey
    },
    askForRecoveryKeys: (state, { payload: { reasons, missingKeysByOwner } }: PayloadAction<{ reasons: string[]; missingKeysByOwner: MissingKeysOwner[] }>) => {
      state.recoveryKeyRequest = { reasons, missingKeysByOwner }
      state.recoveryKeys = undefined
    },
    provideRecoveryKeys: (state, { payload: { recoveryKeys } }: PayloadAction<{ recoveryKeys: string[] }>) => {
      state.recoveryKeys = recoveryKeys
    },
    hideRecoveryKeysWindow: (state) => {
      state.recoveryKeyRequest = undefined
    },
    addTriedRecoveryKeyWithOutcome: (state, { payload: { key, outcome } }: { payload: { key: string; outcome: RecoveryKeyOutcomeEnum } }) => {
      state.triedRecoveryKeys = {
        [key]: outcome,
        ...state.triedRecoveryKeys,
      }
    },
    skipRecoveryKeyCollectionStep: (state) => {
      state.recoveryKeyRequest = undefined
      state.recoveryKeys = undefined
    },
    setRegistrationInformation: (
      state,
      {
        payload: { firstName, lastName, email },
      }: PayloadAction<{
        firstName: string
        lastName: string
        email: string
      }>,
    ) => {
      state.firstName = firstName
      state.lastName = lastName
      state.email = email
    },
    setToken: (state, { payload: { token } }: PayloadAction<{ token: string }>) => {
      state.token = token
      state.invalidToken = false
    },
    setEhealthPassphrase: (state, { payload: { ehealthPassphrase } }: PayloadAction<{ ehealthPassphrase: string }>) => {
      state.ehealthPassphrase = ehealthPassphrase
    },
    setEmail: (state, { payload: { email } }: PayloadAction<{ email: string }>) => {
      state.email = email
      state.invalidEmail = false
    },
    setUser: (state, { payload: { user } }: PayloadAction<{ user: User }>) => {
      state.user = user
      state.online = !!user
    },
    resetCredentials: (state) => {
      state.online = false
    },
    setLoginProcessStarted(state, { payload: status }: PayloadAction<boolean>) {
      state.loginProcessStarted = status
    },
    setWaitingForToken(state, { payload: status }: PayloadAction<boolean>) {
      state.waitingForToken = status
    },
  },
  extraReducers: (builder) => {
    builder.addCase(startAuthentication.fulfilled, (state, { payload: authProcess }) => {
      state.authProcess = authProcess
      state.waitingForToken = true
    })
    builder.addCase(startAuthentication.rejected, (state, {}) => {
      state.invalidEmail = true
    })
    builder.addCase(completeAuthentication.fulfilled, (state, { payload: { user, healthcareParty } }) => {
      state.user = user as User
      state.healthcareParty = { ...healthcareParty } as HealthcareParty
      state.online = !!user
      state.waitingForToken = false
    })
    builder.addCase(completeAuthentication.rejected, (state, {}) => {
      state.invalidToken = true
    })
    builder.addCase(login.fulfilled, (state, { payload: { user, healthcareParty } }) => {
      state.user = user as User
      if (healthcareParty?.id) {
        state.healthcareParty = { ...healthcareParty } as HealthcareParty
      }
      state.online = !!user
    })
    builder.addCase(login.rejected, (state, {}) => {
      state.invalidToken = true
      state.online = false
    })
  },
})

export const {
  addTriedRecoveryKeyWithOutcome,
  hideRecoveryKeysWindow,
  setNewlyCreatedRecoveryKey,
  askForRecoveryKeys,
  provideRecoveryKeys,
  skipRecoveryKeyCollectionStep,
  setRegistrationInformation,
  setToken,
  setEmail,
  resetCredentials,
  setLoginProcessStarted,
  setWaitingForToken,
  setEhealthPassphrase,
} = cardinalApiRtk.actions
